/**
 * Block Clipboard — cross-template copy/paste for blocks
 *
 * Uses localStorage so clipboard persists across template navigations.
 * Each clipboard entry stores a full ContentBlock (deep-cloned).
 */

import type { ContentBlock } from "../components/shared/contentBlockTypes";

const CLIPBOARD_KEY = "zap_block_clipboard";
const CLIPBOARD_MULTI_KEY = "zap_block_clipboard_multi";

/* ─── Single block ─── */

export function copyBlockToClipboard(block: ContentBlock): void {
  try {
    const clone: ContentBlock = {
      ...block,
      meta: block.meta ? JSON.parse(JSON.stringify(block.meta)) : undefined,
    };
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(clone));
    // Clear multi when single is set
    localStorage.removeItem(CLIPBOARD_MULTI_KEY);
  } catch (err) {
    console.error("[blockClipboard] Failed to copy block:", err);
  }
}

export function getClipboardBlock(): ContentBlock | null {
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ContentBlock;
  } catch {
    return null;
  }
}

export function hasClipboardBlock(): boolean {
  return !!localStorage.getItem(CLIPBOARD_KEY) || !!localStorage.getItem(CLIPBOARD_MULTI_KEY);
}

export function clearClipboard(): void {
  localStorage.removeItem(CLIPBOARD_KEY);
  localStorage.removeItem(CLIPBOARD_MULTI_KEY);
}

/* ─── Multi-block (for future multi-select) ─── */

export function copyBlocksToClipboard(blocks: ContentBlock[]): void {
  try {
    const clones = blocks.map((b) => ({
      ...b,
      meta: b.meta ? JSON.parse(JSON.stringify(b.meta)) : undefined,
    }));
    localStorage.setItem(CLIPBOARD_MULTI_KEY, JSON.stringify(clones));
    localStorage.removeItem(CLIPBOARD_KEY);
  } catch (err) {
    console.error("[blockClipboard] Failed to copy blocks:", err);
  }
}

export function getClipboardBlocks(): ContentBlock[] | null {
  try {
    // Multi first
    const multi = localStorage.getItem(CLIPBOARD_MULTI_KEY);
    if (multi) {
      const blocks = JSON.parse(multi) as ContentBlock[];
      return Array.isArray(blocks) ? blocks : null;
    }
    // Fallback to single
    const single = localStorage.getItem(CLIPBOARD_KEY);
    if (single) {
      const block = JSON.parse(single) as ContentBlock;
      return block ? [block] : null;
    }
    return null;
  } catch {
    return null;
  }
}

/* ─── Clipboard change listener (for UI reactivity) ─── */

const listeners = new Set<() => void>();

export function onClipboardChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function notifyClipboardChange(): void {
  listeners.forEach((fn) => fn());
}
